import { useMemo } from 'react';
import { useWakeCallables } from '@officehub/offline-sdk';
import { useAuth } from '../contexts/AuthContext';
import { getOriginalFetch } from '../lib/contextCapture/contextCapture';
import { WAKE_SCREENS, wakeArm, wakeEnabledForDef, wakeUrlFor, type WakeScreen, type WakeScreenDef } from '../config/wakeKnock';

/**
 * A knock is DESIGNED to fail at the browser (see wakeCallables.ts), so it must bypass the
 * app's fetch wrapper, which would log every one as a network error and retry it. Module-level so
 * the reference is stable across renders.
 */
export const knockFetch: typeof fetch = (input, init) => getOriginalFetch()(input, init);

/**
 * The signed-in uid, or null when there is no auth provider above this screen (a test that renders a
 * page bare, a route outside the provider). useAuth() throws without a provider AFTER it has called
 * useContext, so catching the throw keeps the hook order identical on every render. No provider means
 * nobody to wake for, never a crash in a screen that only wanted to warm its servers.
 */
export function useSignedInUid(): string | null {
  try {
    return useAuth().firebaseUser?.uid ?? null;
  } catch {
    return null;
  }
}

/**
 * Call once at the top of a screen: wakes that screen's callables in the background so the first
 * button press does not wait for a sleeping server. Renders nothing; failures are silent.
 * Nobody is knocked for before sign-in (no uid yet), and during the pilot only the user's own arm.
 * Takes a pilot screen key or a module's own definition (declare it as a module-level constant).
 */
export function useWakeScreen(screen: WakeScreen | WakeScreenDef): void {
  const def = typeof screen === 'string' ? WAKE_SCREENS[screen] : screen;
  const uid = useSignedInUid();
  // Memoised on the flag (a boolean), not on `def`, so even an inline object cannot re-subscribe the
  // effect on every render. The callable list is compared by content inside useWakeCallables.
  const defEnabled = def.enabled;
  const enabled = useMemo(() => {
    const screenOn = wakeEnabledForDef({ enabled: defEnabled, callables: [] });
    return () => screenOn() && wakeArm(uid) === 'wake';
  }, [defEnabled, uid]);

  useWakeCallables(def.callables, {
    urlFor: wakeUrlFor,
    enabled,
    fetchImpl: knockFetch,
  });
}

/**
 * Like useWakeScreen, for a screen a person reaches BEFORE signing in (the invitation page). There is no uid to arm on, so
 * the knock goes to everyone; the screen's own flag and the global kill switch still apply. Keep such a definition to the
 * ONE callable the page's main action needs, so a public page cannot be used to warm the whole backend.
 */
export function usePreSignInWake(def: WakeScreenDef): void {
  const defEnabled = def.enabled;
  const enabled = useMemo(() => wakeEnabledForDef({ enabled: defEnabled, callables: [] }), [defEnabled]);
  useWakeCallables(def.callables, { urlFor: wakeUrlFor, enabled, fetchImpl: knockFetch });
}
